import { useState, useEffect, useRef } from "react";
import { Form } from "antd";
import dayjs from "dayjs";
import Button from "@/common/elements/Button";
import CommonDatePicker from "@/common/elements/DatePicker";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { useClientParamBool } from "@/hooks/useClientParam";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import CustomModal from "@/components/CustomModal";
import { TableHeader } from "@/components/InmateDetails/styles";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import {
  getDayjsObject,
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
  convertApiFields,
  getSQLTypeToHTMLType,
  hasCheckSecurity,
} from "@/util/common_function";
import { Arbitrary_Names, Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import DataTable from "@/common/elements/DataTable";
import { useDispatch, useSelector } from "react-redux";
import Popconfirm from "@/common/elements/Popconfirm";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import _ from "lodash";
import { v4 as uuidV4 } from "uuid";
import API_ENDPOINTS from "@/util/api";
import api from "@/services/axios_custom";
import { MODULES_LIST, SCHEMA_NAME } from "@/util/common_variable";
import Administrator from "@/components/JailIncidents/Administrator";
import Supervisor from "@/components/JailIncidents/Supervisor";
import { setJailIncidentStore } from "@/slices/jailIncidents";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";
import {
  fetchExtraTableRecord,
  saveExtraTableRecord,
} from "@/util/extraColumnActions";

// Legacy ListID for this grid depends on which screen is actually using it —
// Bookings' "Officer Reports" tab and Staff's "Staff Supplementals" tab both
// go through the same `from === MODULES_LIST.BOOKINGS` code branch here (see
// below), so `from` alone can't tell them apart; `startingPoint` can.
// Confirmed against legacy: OfficerReportsInterface.asp (`ListID =
// "OfficerReportsSupplementals"`) and StaffInterface.asp (`ListID =
// "StaffSupplementals"`).
const BOOKINGS_BRANCH_LIST_IDS = {
  Staff: "StaffSupplementals",
  Bookings: "OfficerReportsSupplementals",
};

// Facility Log's nested Supplementals grid — confirmed against legacy
// CommentLogInterface.asp (`ListID = "CommentLogSupplementals"`, label
// "List of supplementals to this log entry", matching the
// supplementalListHeading prop exactly). Legacy's own GetModuleName() for
// this screen is actually "Comment Log" (a different per-entry-detail
// screen from "Facility Log", the search/list page) — deliberately not
// replicated here, same simplification as Bookings-Queue-vs-Bookings: React
// has no separate "Comment Log" concept, so "Facility Log" is used as the
// single stable module prefix for both.
const FACILITY_LOG_LIST_ID = "CommentLogSupplementals";

// Jail Incidents' nested Supplementals grid — confirmed against legacy
// JailIncidentsInterface.asp (`ListID = "JailIncidentsSupplementals"`,
// label "List of supplementals to this log entry", matching
// supplementalListHeading exactly). Hardcoded rather than threaded through
// startingPoint: the only caller (components/JailIncidents/Supplementals.jsx)
// always passes startingPoint="JailIncidents" (no space), which doesn't
// match legacy's "Jail Incidents" GetModuleName() — there's no varying
// context to preserve here, so hardcoding the correct value is simpler and
// avoids the mismatch entirely.
const JAIL_INCIDENTS_LIST_ID = "JailIncidentsSupplementals";

const SupplementalDetails = ({
  report = {},
  showAgencyInfo = false,
  supplementalListHeading = "List of supplementals to this officer report",
  from = MODULES_LIST.BOOKINGS,
  onSave = null,
  oldSupplimentals = [],
  startingPoint="",
}) => {
  const dispatch = useDispatch();
  // ASP: Globals/SupplementalMixedCase (default True)
  const supplementalMixedCase = useClientParamBool("Globals", "SupplementalMixedCase", true);
  const { bookings, bookingsFieldsEnabled } = useSelector((s) => s?.bookings);
  const { jailIncident } = useSelector((s) => s?.jailIncident);

  const [supplementsList, setSupplementsList]         = useState([]);
  const [showSupplementModal, setShowSupplementModal] = useState(false);
  const [selectedSupplement, setSelectedSupplement]   = useState(null);
  const [supplementsListRows, setSupplementsListRows] = useState([]);
  const [loading, setLoading]                         = useState(false);
  const [oldSupplimentalsData, setOldSupplimentalsData] = useState([]);
  const [form] = Form.useForm();

  // Only used by isSupplementLockedByOwnApproval below (per-row check, not
  // a main-record lock) — Supplements buttons themselves are never disabled
  // by the main record's approval status, unlike Involved Inmates.
  const hasAdminPermission = hasCheckSecurity(
    Security_Types.functionality,
    Entity_types.arbitary,
    LABELS_CAPITAL.VIEW,
    Arbitrary_Names.AdminView,
  );

  // -----------------------------------------------------------------------
  // isDisabled — master business-rule disable for buttons/Form.
  // isModify   — whether the modal form is in edit vs view mode
  // -----------------------------------------------------------------------
  const isDisabled =
    from === MODULES_LIST.BOOKINGS ||
    from === MODULES_LIST.JAIL_INCIDENTS ||
    from === MODULES_LIST.FACILITY_LOG
      ? !bookingsFieldsEnabled
      : false;

  const [isModify, setIsModify] = useState(isDisabled);

  // -----------------------------------------------------------------------
  // JailIncidentsSupplementalsExtra — PCI's per-client dynamic "_U" columns
  // for one Jail Incidents supplemental entry
  // (JailIncidentsSupplementalsInterface.asp's "Agency-Specific Supplemental
  // Information" section). Only relevant for the JAIL_INCIDENTS branch —
  // see util/extraColumnActions.js for the generic fetch/save helpers.
  // -----------------------------------------------------------------------
  const [jailIncidentSupplementalExtraFields, setJailIncidentSupplementalExtraFields] = useState([]);
  const jailIncidentSupplementalExtraTypeMapRef = useRef({}); // columnName -> htmlType
  const jailIncidentSupplementalExtraIdRef = useRef(0); // JailIncidentsSupplementalExtraID, decides CREATE vs UPDATE

  useEffect(() => {
    if (from !== MODULES_LIST.JAIL_INCIDENTS) return;
    api
      .get(`${API_ENDPOINTS.getTableColumns}/JailIncidentsSupplementalsExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) => d?.columnName?.endsWith("_U"));
        const converted = convertApiFields(tableData, "JailIncidentsSupplementalsExtra");
        const typeMap = {};
        converted.forEach((c) => { typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type); });
        jailIncidentSupplementalExtraTypeMapRef.current = typeMap;
        setJailIncidentSupplementalExtraFields(converted);
      })
      .catch((error) => {
        console.error("[SupplementalDetails] JailIncidentsSupplementalsExtra field defs fetch failed:", error);
      });
  }, [from]);

  // Fetch the saved extra row once a supplemental with a real
  // jailIncidentsSupplementalId is selected for edit/view — a brand new
  // supplemental has no id yet, so there's nothing to fetch (and any extra
  // values typed into it before the first save simply aren't persisted
  // here; see the save-side gate in handleSubmit below).
  useEffect(() => {
    if (from !== MODULES_LIST.JAIL_INCIDENTS) return;
    const id = selectedSupplement?.supplementals?.[0]?.jailIncidentsSupplementalId;
    if (!isNonEmptyVariable(id)) {
      jailIncidentSupplementalExtraIdRef.current = 0;
      return;
    }

    fetchExtraTableRecord("JailIncidentsSupplementalsExtra", "jailIncidentsSupplementalID", id)
      .then((result) => {
        if (!result) return;
        jailIncidentSupplementalExtraIdRef.current = result.raw?.JailIncidentsSupplementalExtraID ?? 0;
        const withDates = Object.keys(result.values).reduce((acc, key) => {
          const val = result.values[key];
          const htmlType = jailIncidentSupplementalExtraTypeMapRef.current[key];
          const isDateField = htmlType === "date" || htmlType === "datetime";
          acc[key] = isDateField && isNonEmptyVariable(val) ? dayjs(val) : val;
          return acc;
        }, {});
        // Merge onto whatever onEditClick already put in supplementals[0]
        // rather than replacing it outright, so the base fields (filing
        // officer, filing date, ...) aren't clobbered.
        form.setFieldsValue({
          supplementals: [{ ...(form.getFieldValue("supplementals")?.[0] || {}), ...withDates }],
        });
      })
      .catch((error) => {
        console.error("[SupplementalDetails] JailIncidentsSupplementalsExtra fetch failed:", error);
      });
  }, [selectedSupplement]);

  // -----------------------------------------------------------------------
  // Table headers — varies by module
  // -----------------------------------------------------------------------
  const defaultHeaders = [
    { key: "id",                  text: "#"             },
    { key: "filingDate",          text: "Filing Date"   },
    { key: "filingOfficerStaff",  text: "Filing Officer"},
    { key: "fBadge",              text: "Badge"         },
    { key: "enteredByStaff",      text: "Entered By"    },
    { key: "eBadge",              text: "Badge"         },
  ];

  const facilityHeaders = [
    { key: "id",                 text: "#"             },
    { key: "filingDate",         text: "Filing Date"   },
    { key: "filingOfficerStaff", text: "Filing Officer"},
    { key: "enteredByStaff",     text: "Entered By"    },
    { key: "supplemental",       text: "supplemental"  },
  ];

  const jailIncidentHeaders = [
    { key: "jailIncidentsSupplementalId", text: "#"             },
    { key: "filingDate",                  text: "Filing Date"   },
    { key: "filingOfficerStaff",          text: "Filing Officer"},
    { key: "enteredByStaff",              text: "Entered By"    },
    { key: "supplemental",                text: "supplemental"  },
  ];

  const tableHeaders =
    from === MODULES_LIST.FACILITY_LOG    ? facilityHeaders :
    from === MODULES_LIST.JAIL_INCIDENTS  ? jailIncidentHeaders :
    defaultHeaders;

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). Enabled for all three
  // confirmed branches — Bookings/Staff (BOOKINGS_BRANCH_LIST_IDS), Facility
  // Log (FACILITY_LOG_LIST_ID), and Jail Incidents (JAIL_INCIDENTS_LIST_ID).
  const supplementalsGridId =
    from === MODULES_LIST.BOOKINGS
      ? `${startingPoint || "Bookings"}:${
          BOOKINGS_BRANCH_LIST_IDS[startingPoint] ??
          BOOKINGS_BRANCH_LIST_IDS.Bookings
        }`
      : from === MODULES_LIST.FACILITY_LOG
        ? `${startingPoint || "Facility Log"}:${FACILITY_LOG_LIST_ID}`
        : from === MODULES_LIST.JAIL_INCIDENTS
          ? `Jail Incidents:${JAIL_INCIDENTS_LIST_ID}`
          : null;

  const {
    headers: configuredHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef: configDataTableRef,
  } = useGridColumnConfig(supplementalsGridId, tableHeaders);

  // -----------------------------------------------------------------------
  // Init — set default filing officer from local storage
  // -----------------------------------------------------------------------
  useEffect(() => {
    const loginUser = JSON.parse(localStorage.getItem("userDetails"));
    form.setFieldsValue({
      supplementals: [{
        filingOfficerStaffId: loginUser?.staffId ?? 1,
      }],
    });
  }, []);

  // -----------------------------------------------------------------------
  // Sync report prop → table rows (for FACILITY_LOG / read-only view)
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!isNonEmptyVariable(report)) return;
    const supplementalsGroups = report?.officerReportsSupplementals || [];
    const filingOfficerOptions = getOptionsFromRefList("Filing_Officer", true, true, true) || [];

    const rows = supplementalsGroups
      .map((group, index) => {
        if (!group?.supplementals?.length) return null;
        const sup = group.supplementals[0];
        const filingOfficerName =
          filingOfficerOptions.find((o) => o.value === sup.filingOfficerStaffId)?.label ??
          sup.filingOfficerStaffId;
        const enteredByName =
          filingOfficerOptions.find((o) => o.value === sup.enteredByStaffId)?.label ??
          sup.enteredByStaffId;
        return {
          id:                   index + 1,
          filingDate:           sup.filingDate        || "",
          filingOfficerStaffId: filingOfficerName,
          enteredByStaffId:     enteredByName,
          supplementalText:     sup.supplemental       || "",
          fullData:             sup,
        };
      })
      .filter(Boolean);

    if (rows.length) setSupplementsListRows(rows);
  }, [report]);

  // -----------------------------------------------------------------------
  // Sync oldSupplimentals → rows
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (oldSupplimentals?.length) setOldSupplimentalsData([...oldSupplimentals]);
  }, [oldSupplimentals]);

  useEffect(() => {
    if (!oldSupplimentalsData?.length) return;
    setSupplementsListRows(
      oldSupplimentalsData.map((e, i) => ({
        ...e,
        id:                i + 1,
        uuid:              uuidV4(),
        filingOfficerStaff: isNonEmptyVariable(e?.filingOfficerStaffId)
          ? getRefListNameByID("", e.filingOfficerStaffId, true) : "",
        enteredByStaff: isNonEmptyVariable(e?.enteredByStaffId)
          ? getRefListNameByID("", e.enteredByStaffId, true) : "",
      }))
    );
  }, [oldSupplimentalsData]);

  // -----------------------------------------------------------------------
  // Process supplementals from Redux (async badge lookups)
  // -----------------------------------------------------------------------
  async function getBatchOfUser(staffId) {
    if (!isNonEmptyVariable(staffId)) return "";
    const resp = await api.get(API_ENDPOINTS.staff.staffByID + staffId);
    return resp?.data?.badge || "";
  }

  useEffect(() => {
    const processSupplementals = async () => {
      setLoading(true);
      try {
        if (from === MODULES_LIST.BOOKINGS && isNonEmptyVariable(bookings)) {
          const sp = await Promise.all(
            (bookings?.staffSupplementals || []).map(async (s, index) => {
              const sp2 = await Promise.all(
                (s?.supplementals || []).map(async (e) => {
                  const [fBadge, eBadge] = await Promise.all([
                    getBatchOfUser(e?.filingOfficerStaffId),
                    getBatchOfUser(e?.enteredByStaffId),
                  ]);
                  return {
                    ...e, id: index + 1, uuid: uuidV4(),
                    filingOfficerStaff: isNonEmptyVariable(e?.filingOfficerStaffId)
                      ? getRefListNameByID("", e.filingOfficerStaffId, true) : "",
                    fBadge: fBadge || "",
                    enteredByStaff: isNonEmptyVariable(e?.enteredByStaffId)
                      ? getRefListNameByID("", e.enteredByStaffId, true) : "",
                    eBadge: eBadge || "",
                  };
                })
              );
              return { staffSupplementalId: s?.staffSupplementalId, supplementals: sp2 };
            })
          );
          setSupplementsList(sp);
        } else if (from === MODULES_LIST.JAIL_INCIDENTS && isNonEmptyVariable(jailIncident)) {
          const sp = await Promise.all(
            (jailIncident?.jailIncidentsSupplementals || []).map(async (s, index) => {
              const sp2 = await Promise.all(
                (s?.supplementals || []).map(async (e) => {
                  const [fBadge, eBadge] = await Promise.all([
                    getBatchOfUser(e?.filingOfficerStaffId),
                    getBatchOfUser(e?.enteredByStaffId),
                  ]);
                  return {
                    ...e, id: index + 1, uuid: uuidV4(),
                    filingOfficerStaff: isNonEmptyVariable(e?.filingOfficerStaffId)
                      ? getRefListNameByID("", e.filingOfficerStaffId, true, true) : "",
                    fBadge: fBadge || "",
                    enteredByStaff: isNonEmptyVariable(e?.enteredByStaffId)
                      ? getRefListNameByID("", e.enteredByStaffId, true, true) : "",
                    eBadge: eBadge || "",
                    administratorDateTime: getDayjsObject(e?.administratorDate, e?.administratorTime),
                    supervisorDateTime:    getDayjsObject(e?.supervisorDate,    e?.supervisorTime),
                  };
                })
              );
              return { jailIncidentsSupplementalId: s?.jailIncidentsSupplementalId, supplementals: sp2 };
            })
          );
          setSupplementsList(sp);
        } else {
          setSupplementsList([]);
        }
      } finally {
        setLoading(false);
      }
    };
    processSupplementals();
  }, [bookings, jailIncident]);

  // -----------------------------------------------------------------------
  // Flatten supplementsList → row data for DataTable
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (from === MODULES_LIST.BOOKINGS) {
      setSupplementsListRows(
        supplementsList.map((e) => {
          const e1 = _.cloneDeep(e);
          if (e1?.supplementals?.length) {
            e1.supplementals[0] = { ...e1.supplementals[0], staffSupplementalId: e1.staffSupplementalId };
          }
          return e1?.supplementals[0];
        })
      );
    } else if (from === MODULES_LIST.JAIL_INCIDENTS) {
      setSupplementsListRows(
        supplementsList.map((e) => {
          const e1 = _.cloneDeep(e);
          if (e1?.supplementals?.length) {
            e1.supplementals[0] = { ...e1.supplementals[0], jailIncidentsSupplementalId: e1.jailIncidentsSupplementalId };
          }
          return e1?.supplementals[0];
        })
      );
    }
  }, [supplementsList]);

  // -----------------------------------------------------------------------
  // Handlers
  // -----------------------------------------------------------------------

  // Same rule as JailIncidentsInvolvedDetail/index.jsx's lockedByOwnApproval:
  // once THIS specific supplement's own Administrator status is Approved,
  // the whole record locks for everyone (Admin included). Once only its
  // Supervisor status is Approved, it locks for everyone EXCEPT a user with
  // Admin-View permission. Takes the raw row object directly (rather than
  // reading `selectedSupplement` state) so onRowClick can check the row it
  // was just handed instead of the pre-update state value.
  const isSupplementLockedByOwnApproval = (row) => {
    const administratorStatus = row?.administratorStatus?.trim?.();
    const supervisorStatus = row?.supervisorStatus?.trim?.();
    return (
      administratorStatus === "Approved" ||
      (supervisorStatus === "Approved" && !hasAdminPermission)
    );
  };

  const onRowClick = (value, index) => {
    if (isNonEmptyVariable(value)) {
      setSelectedSupplement({
        supplementals: [{
          ...value,
          filingDate: isNonEmptyVariable(value?.filingDate) ? dayjs(value.filingDate) : null,
        }],
      });
    }
    if (index !== undefined && index !== null) {
      const locked = isSupplementLockedByOwnApproval(value);
      onEditClick(bookingsFieldsEnabled && !locked ? "edit" : "view");
    }
  };

  const onAddClick = () => {
    setIsModify(true);
    setSelectedSupplement(null);
    setShowSupplementModal(true);
  };

  const onEditClick = (action) => {
    setIsModify(action !== "view");
    setShowSupplementModal(true);
    form.setFieldsValue(selectedSupplement);
  };

  // Details opens editable when the record is in Modify mode and this
  // specific supplement isn't itself already Approved, read-only otherwise
  // - same rule onRowClick already applies (see InvolvedInmates.jsx's
  // identical openDetails), now shared by the Details button too since
  // Details/Modify collapsed into one button. Unlike Involved Inmates, the
  // main record's own approval status never locks these buttons.
  const openDetails = () => {
    const locked = isSupplementLockedByOwnApproval(
      selectedSupplement?.supplementals?.[0],
    );
    onEditClick(bookingsFieldsEnabled && !locked ? "edit" : "view");
  };

  const handleCancel = () => {
    setSelectedSupplement(null);
    setShowSupplementModal(false);
    configDataTableRef?.current?.resetAll();
    form.resetFields();
  };

  const updateForm = (data) => {
    setSelectedSupplement((prev) => ({
      ...(prev || {}),
      supplementals: [{ ...(prev?.supplementals?.[0] || {}), ...data }],
    }));
  };

  const handleSubmit = () => {
    form.validateFields()
      .then(async (data) => {
        let data2 = data?.supplementals[0];
        if (data2?.filingDate) data2.filingDate = dayjs(data2.filingDate).format("MM/DD/YYYY");

        if (from === MODULES_LIST.BOOKINGS) {
          let listData = _.cloneDeep(supplementsList);
          if (isNonEmptyVariable(selectedSupplement)) {
            listData = listData.map((m) => {
              if (m?.supplementals[0]?.uuid === selectedSupplement?.supplementals[0]?.uuid) {
                m.supplementals[0] = { ...m.supplementals[0], ...data.supplementals[0] };
                return { ...m };
              }
              return m;
            });
          } else {
            listData.push(data);
          }
          setSupplementsList(listData);
          dispatch(setBookings({ staffSupplementals: listData }));

        } else if (from === MODULES_LIST.FACILITY_LOG) {
          let listData = _.cloneDeep(supplementsListRows);
          const newData = data?.supplementals[0];
          if (isNonEmptyVariable(selectedSupplement)) {
            const newSupplimental = selectedSupplement?.supplementals[0];
            if (isNonEmptyVariable(newSupplimental)) {
              const updated = listData.map((e) => {
                if (e?.uuid === newSupplimental?.uuid) {
                  return {
                    ...e, ...newData,
                    filingOfficerStaff: isNonEmptyVariable(newData?.filingOfficerStaffId)
                      ? getRefListNameByID("", newData.filingOfficerStaffId, true) : "",
                    enteredByStaff: isNonEmptyVariable(newData?.enteredByStaffId)
                      ? getRefListNameByID("", newData.enteredByStaffId, true) : "",
                  };
                }
                return { ...e };
              });
              setOldSupplimentalsData(updated);
              setSelectedSupplement(null);
              onSave(updated);
            }
          } else if (isNonEmptyVariable(newData)) {
            oldSupplimentals.push(newData);
            setOldSupplimentalsData([...oldSupplimentalsData, newData]);
            onSave(oldSupplimentals);
          }

        } else if (from === MODULES_LIST.JAIL_INCIDENTS) {
          // Split JailIncidentsSupplementalsExtra's dynamic fields out of
          // data.supplementals[0] — they ride the same nested
          // ["supplementals", 0, columnName] form path as the base fields
          // (see the render block below) for simplicity, but are saved
          // separately via the generic extra-table endpoint rather than
          // folded into the deferred jailIncidentsSupplementals payload.
          const jailIncidentExtraFieldKeys = jailIncidentSupplementalExtraFields.map((f) => f.columnName);
          const jailIncidentExtraRecord = {};
          jailIncidentExtraFieldKeys.forEach((key) => {
            if (key in (data?.supplementals?.[0] || {})) {
              const val = data.supplementals[0][key];
              const htmlType = jailIncidentSupplementalExtraTypeMapRef.current[key];
              jailIncidentExtraRecord[key] = dayjs.isDayjs(val)
                ? val.format(htmlType === "datetime" ? "MM/DD/YYYY HH:mm" : "MM/DD/YYYY")
                : val;
              delete data.supplementals[0][key];
            }
          });

          // Only an existing supplemental (a real jailIncidentsSupplementalId)
          // can be saved immediately — a brand new one only gets that id
          // once the whole Jail Incident record is saved, which this modal
          // doesn't wait for.
          const existingSupplementalId = selectedSupplement?.supplementals?.[0]?.jailIncidentsSupplementalId;
          if (jailIncidentExtraFieldKeys.length > 0 && isNonEmptyVariable(existingSupplementalId)) {
            saveExtraTableRecord({
              tableName: "JailIncidentsSupplementalsExtra",
              idColumn: "jailIncidentsSupplementalID",
              idValue: existingSupplementalId,
              extraRecord: {
                ...jailIncidentExtraRecord,
                jailIncidentsSupplementalExtraID: jailIncidentSupplementalExtraIdRef.current || undefined,
              },
              extraIdKey: "jailIncidentsSupplementalExtraID",
            }).catch((error) => {
              console.error("[SupplementalDetails] JailIncidentsSupplementalsExtra save failed:", error);
            });
          }

          const supervisorAdmin = {
            supervisorDate:              selectedSupplement?.supplementals?.[0]?.supervisorDate              || null,
            supervisorTime:              selectedSupplement?.supplementals?.[0]?.supervisorTime              || null,
            supervisorReject:            selectedSupplement?.supplementals?.[0]?.supervisorReject            || null,
            supervisorRejectReason:      selectedSupplement?.supplementals?.[0]?.supervisorRejectReason      || null,
            supervisorStaffId:           selectedSupplement?.supplementals?.[0]?.supervisorStaffId           || null,
            supervisorStatus:            selectedSupplement?.supplementals?.[0]?.supervisorStatus            || null,
            administratorDate:           selectedSupplement?.supplementals?.[0]?.administratorDate           || null,
            administratorReject:         selectedSupplement?.supplementals?.[0]?.administratorReject         || null,
            administratorRejectReason:   selectedSupplement?.supplementals?.[0]?.administratorRejectReason   || null,
            administratorStaffId:        selectedSupplement?.supplementals?.[0]?.administratorStaffId        || null,
            administratorStatus:         selectedSupplement?.supplementals?.[0]?.administratorStatus         || null,
            administratorTime:           selectedSupplement?.supplementals?.[0]?.administratorTime           || null,
          };

          let listData = _.cloneDeep(supplementsList);
          if (isNonEmptyVariable(selectedSupplement?.supplementals[0]?.uuid)) {
            listData = listData.map((m) => {
              if (m?.supplementals[0]?.uuid === selectedSupplement?.supplementals[0]?.uuid) {
                m.supplementals[0] = { ...m.supplementals[0], ...data.supplementals[0], ...supervisorAdmin };
                return { ...m };
              }
              return m;
            });
          } else {
            listData.push({ ...data, supplementals: [{ ...data.supplementals[0], ...supervisorAdmin }] });
          }
          setSupplementsList(listData);
          dispatch(setJailIncidentStore({ jailIncidentsSupplementals: listData }));
        }
      })
      .catch(console.log)
      .finally(handleCancel);
  };

  const handleDelete = () => {
    try {
      if (from === MODULES_LIST.BOOKINGS) {
        let listData = _.cloneDeep(supplementsList).filter(
          (m) => m?.supplementals[0]?.uuid !== selectedSupplement?.supplementals[0]?.uuid
        );
        if (isNonEmptyVariable(selectedSupplement?.supplementals[0]?.staffSupplementalId)) {
          dispatch(setDeleteAPis(
            API_ENDPOINTS.staff.supplementsList.delete + selectedSupplement.supplementals[0].staffSupplementalId
          ));
        }
        setSupplementsList(listData);
        dispatch(setBookings({ staffSupplementals: [...listData] }));

      } else if (from === MODULES_LIST.JAIL_INCIDENTS) {
        let listData = _.cloneDeep(supplementsList).filter(
          (m) => m?.supplementals[0]?.uuid !== selectedSupplement?.supplementals[0]?.uuid
        );
        if (isNonEmptyVariable(selectedSupplement?.supplementals?.[0]?.jailIncidentsSupplementalId)) {
          dispatch(setDeleteAPis(
            API_ENDPOINTS.jailIncidents.deleteSupplements + selectedSupplement.supplementals[0].jailIncidentsSupplementalId
          ));
        }
        setSupplementsList(listData);
        dispatch(setJailIncidentStore({ jailIncidentsSupplementals: [...listData] }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      handleCancel();
    }
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <>
      <StyledSubTableContainer>
        <TableHeader justify="space-between">
          <h2>{supplementalListHeading}</h2>
          <StyledFlex gap="10px">
            <Button color="primary" type="primary" onClick={onAddClick} disabled={isDisabled || isConfiguring}>
              Add
            </Button>
            <Button color="primary" type="primary" onClick={openDetails} disabled={selectedSupplement === null || isConfiguring}>
              Details
            </Button>
            <Popconfirm
              title="Delete the supplement"
              description="Are you sure to delete this supplement?"
              onConfirm={handleDelete}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              {from !== MODULES_LIST.FACILITY_LOG && (
                <Button color="primary" type="primary" disabled={selectedSupplement === null || isDisabled || isConfiguring}>
                  Delete
                </Button>
              )}
            </Popconfirm>
            {/* Config — drag-reorder/resize columns; only enabled where the
                legacy ListID is confirmed (see supplementalsGridId above) */}
            {supplementalsGridId && (
              !isConfiguring ? (
                <Button color="primary" type="primary" htmlType="button" onClick={startConfiguring}>
                  {configButtonLabel}
                </Button>
              ) : (
                <Popconfirm
                  title="Do you want to save the current list view configuration?"
                  onConfirm={confirmSave}
                  onCancel={discardChanges}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button color="primary" type="primary" htmlType="button">
                    {configButtonLabel}
                  </Button>
                </Popconfirm>
              )
            )}
          </StyledFlex>
        </TableHeader>

        <DataTable
          ref={configDataTableRef}
          rowUniqueId="uuid"
          headers={configuredHeaders}
          draggable={isConfiguring}
          data={supplementsListRows || []}
          onRowClick={onRowClick}
          selected={selectedSupplement === null}
          allowTableSorting
          isAPiCallLoading={loading}
        />
      </StyledSubTableContainer>

      {/* ── Supplemental Modal ─────────────────────────────────────── */}
      {showSupplementModal && (
        <CustomModal
          show={showSupplementModal}
          header="Supplemental Details"
          size="xl"
          onHide={() => { setShowSupplementModal(false); setSelectedSupplement(null); handleCancel(); }}
          onSaveClick={handleSubmit}
          isDisabled={!isModify}
        >
          {/*
            bookingsFieldsEnabled={bookingsFieldsEnabled} — provider respects master flag.
            overrideDisabled={!isModify} on each field handles the view vs edit mode.
          */}
          <FieldSecurityProvider
            entity={SCHEMA_NAME.SUPPLEMENTALS}
            bookingsFieldsEnabled={bookingsFieldsEnabled}
            startingPoint={startingPoint}
          >
            <Form form={form} layout="vertical" disabled={isDisabled}>
              <StyledSectionHeading>
                <span>Supplemental Information</span>
              </StyledSectionHeading>

              <StyledGridRow gutter={16} justify="start">

                {/* Filing Officer */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName={["supplementals", 0, "filingOfficerStaffId"]}
                    overrideDisabled={!isModify}
                    formItemProps={{ name: ["supplementals", 0, "filingOfficerStaffId"], label: "Filing Officer" }}
                  >
                    <Select
                      options={getOptionsFromRefList("Filing_Officer", true, false, true)}
                      placeholder="Select Filing Officer"
                    />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Filing Date */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName="filingDate"
                    overrideDisabled={!isModify}
                    formItemProps={{ name: ["supplementals", 0, "filingDate"], label: "Filing Date" }}
                  >
                    <CommonDatePicker format="MM/DD/YYYY" showTime={false} />
                  </SecureFormItem>
                </StyledGridCol>

                {/* Entered By */}
                <StyledGridCol xs={6}>
                  <SecureFormItem
                    fieldName={["supplementals", 0, "enteredByStaffId"]}
                    overrideDisabled={!isModify}
                    formItemProps={{ name: ["supplementals", 0, "enteredByStaffId"], label: "Entered By" }}
                  >
                    <Select
                      options={getOptionsFromRefList("Filing_Officer", true, false, true)}
                      placeholder="Select Entered By"
                    />
                  </SecureFormItem>
                </StyledGridCol>

              </StyledGridRow>

              <StyledGridRow gutter={16}>
                {/* Supplemental text */}
                <StyledGridCol xs={24}>
                  <SecureFormItem
                    fieldName="supplemental"
                    overrideDisabled={!isModify}
                    formItemProps={{ name: ["supplementals", 0, "supplemental"], label: "Supplemental" }}
                  >
                    <TextField inputType="textarea" rows={5} mixedCase={supplementalMixedCase} />
                  </SecureFormItem>
                </StyledGridCol>
              </StyledGridRow>

              {/* Jail Incidents — Supervisor & Administrator sections */}
              {from === MODULES_LIST.JAIL_INCIDENTS && (
                <Supervisor
                  data={selectedSupplement?.supplementals?.[0]}
                  parentFieldEnable={isModify}
                  fromkey="supplementals"
                  updateForm={updateForm}
                />
              )}
              {from === MODULES_LIST.JAIL_INCIDENTS && (
                <Administrator
                  data={selectedSupplement?.supplementals?.[0]}
                  parentFieldEnable={isModify}
                  fromkey="supplementals"
                  updateForm={updateForm}
                />
              )}

              {/* Jail Incidents — JailIncidentsSupplementalsExtra (legacy
                  heading is literally "Agency-Specific Supplemental
                  Information Information" — a duplicated word in
                  JailIncidentsSupplementalsInterface.asp itself; trimmed
                  here rather than reproduced verbatim) */}
              {from === MODULES_LIST.JAIL_INCIDENTS &&
                jailIncidentSupplementalExtraFields.length > 0 && (
                  <>
                    <StyledSectionHeading>
                      <span>Agency-Specific Supplemental Information</span>
                    </StyledSectionHeading>
                    <StyledGridRow gutter={16}>
                      {jailIncidentSupplementalExtraFields.map((sf) => {
                        const htmlType = getSQLTypeToHTMLType(sf.type);
                        return (
                          <StyledGridCol lg={6} key={sf.columnName}>
                            <SecureFormItem
                              fieldName={sf.columnName}
                              overrideDisabled={!isModify}
                              formItemProps={{
                                name: ["supplementals", 0, sf.columnName],
                                label: sf.label,
                                valuePropName:
                                  htmlType === "checkbox" ? "checked" : "value",
                              }}
                            >
                              {htmlType === "textarea" ? (
                                <TextField inputType="textarea" rows={3} />
                              ) : htmlType === "select" ? (
                                <Select
                                  options={getOptionsFromRefList(
                                    sf.refListName || sf.columnName,
                                    sf.isStaff ? true : false,
                                    false,
                                    sf.isID ? true : false,
                                  )}
                                  placeholder={sf.label}
                                  showSearch
                                />
                              ) : htmlType === "checkbox" ? (
                                <Checkbox />
                              ) : htmlType === "date" ? (
                                <CommonDatePicker format="MM/DD/YYYY" showTime={false} />
                              ) : htmlType === "datetime" ? (
                                <CommonDatePicker format="MM/DD/YYYY HH:mm" showTime={true} />
                              ) : (
                                <TextField />
                              )}
                            </SecureFormItem>
                          </StyledGridCol>
                        );
                      })}
                    </StyledGridRow>
                  </>
                )}

              {/* Agency-specific section */}
              {showAgencyInfo && (
                <>
                  <StyledSectionHeading>
                    <span>Agency-Specific General Supplemental Information</span>
                  </StyledSectionHeading>
                  <StyledGridRow gutter={16}>
                    <StyledGridCol lg={12}>
                      <SecureFormItem
                        fieldName="agencySupplementalCodeU"
                        overrideDisabled={!isModify}
                        formItemProps={{
                          name: ["supplementals", 0, "agencySupplementalCodeU"],
                          label: "Agency Supplemental Code",
                        }}
                      >
                        <TextField id="agencySupplementalCode" />
                      </SecureFormItem>
                    </StyledGridCol>
                  </StyledGridRow>
                </>
              )}

            </Form>
          </FieldSecurityProvider>
        </CustomModal>
      )}
    </>
  );
};

export default SupplementalDetails;